import { html, nothing } from "lit";
import { icons } from "../icons.ts";
import {
  formatAttachmentBytes,
  mediaTypeLabel,
  sessionFileIconName,
  type SessionFileRecord,
} from "../chat/session-files.ts";

/**
 * "Session uploads" — the inbound half of the Files tab.
 *
 * Project files are what tools WROTE. Session uploads are what the operator
 * ATTACHED. They are separate stores and used to be conflated: the tab only
 * knew about diffs, so a 2.1 MiB mp4 that had already been stored and read
 * by the model still showed "No recorded changes yet" and "0 files". This
 * section sits above the change list and answers "what did I give this
 * session, and what did it prepare from it?".
 *
 * Every row, including files from earlier turns, is listed here. Opening a
 * row previews the stored bytes and can jump to the transcript message that
 * referenced it.
 */

export type SessionUploadsPanelProps = {
  loading: boolean;
  error: string | null;
  files: SessionFileRecord[];
  /** Build the preview/raw URL for a stored file. Host-supplied. */
  resolveUrl: (file: SessionFileRecord, variant: "raw" | "preview") => string;
  /** Jump to the transcript message that referenced this file. */
  onOpenInTranscript?: (file: SessionFileRecord) => void;
  onRefresh: () => void;
  connected: boolean;
};

/** `2.1 MB · MP4 · 1:04` — the facts worth showing next to a filename. */
function factsLine(file: SessionFileRecord): string {
  const parts: string[] = [formatAttachmentBytes(file.size)];
  const label = mediaTypeLabel(file.mediaType);
  if (label) parts.push(label);
  const prep = file.prep;
  if (prep?.durationSeconds !== undefined) {
    parts.push(`${Math.round(prep.durationSeconds)}s`);
  } else if (prep?.pageCount !== undefined) {
    parts.push(`${prep.pageCount} page${prep.pageCount === 1 ? "" : "s"}`);
  } else if (prep?.rowCount !== undefined) {
    parts.push(`${prep.rowCount} row${prep.rowCount === 1 ? "" : "s"}`);
  } else if (prep?.frameCount !== undefined) {
    parts.push(`${prep.frameCount} frame${prep.frameCount === 1 ? "" : "s"}`);
  }
  return parts.join(" · ");
}

/** One-line summary of what the preparation node produced. */
function prepLine(file: SessionFileRecord): string | null {
  const prep = file.prep;
  if (!prep) return null;
  if (prep.binaryOnly) return "stored · not inlined";
  const bits: string[] = [];
  if (prep.columns?.length) bits.push(`${prep.columns.length} columns`);
  if (prep.textChars) bits.push(`${prep.textChars} chars extracted`);
  if (prep.derivatives.length) bits.push(`${prep.derivatives.length} derivative${prep.derivatives.length === 1 ? "" : "s"}`);
  return bits.length > 0 ? `prepared · ${bits.join(" · ")}` : "prepared";
}

export function renderSessionUploadsPanel(props: SessionUploadsPanelProps) {
  const files = props.files;
  return html`
    <div class="fleet-session-uploads" data-testid="session-uploads">
      <div class="fleet-session-uploads__header">
        <span class="fleet-session-uploads__title">Session uploads</span>
        <span class="fleet-session-uploads__meta muted">
          ${props.loading ? "Loading…" : `${files.length} file${files.length === 1 ? "" : "s"}`}
        </span>
        <button
          type="button"
          class="btn btn--sm"
          ?disabled=${!props.connected || props.loading}
          @click=${props.onRefresh}
        >
          Refresh
        </button>
      </div>
      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}
      ${
        props.loading
          ? html`<p class="muted">Loading uploads…</p>`
          : files.length === 0
            ? html`
                <p class="muted">
                  Nothing attached to this session yet. Files you add in the composer are stored here
                  and stay listed across turns and reloads.
                </p>
              `
            : html`
                <ul class="fleet-session-uploads__list" role="list">
                  ${files.map((file) => {
                    const isImage = file.mediaType.startsWith("image/");
                    const preview = file.previewMediaType
                      ? props.resolveUrl(file, "preview")
                      : "";
                    const raw = props.resolveUrl(file, "raw");
                    return html`
                      <li class="fleet-session-uploads__row" role="listitem">
                        <a
                          class="fleet-session-uploads__open"
                          href=${raw}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="session-upload-row"
                          data-session-file-id=${file.fileId}
                          title=${`Open ${file.filename} (${file.mediaType}, ${formatAttachmentBytes(file.size)})`}
                        >
                          ${preview
                            ? html`<img
                                class="fleet-session-uploads__thumb"
                                src=${preview}
                                alt=${`Preview of ${file.filename}`}
                                loading="lazy"
                              />`
                            : html`<span
                                class="fleet-session-uploads__icon"
                                aria-hidden="true"
                                >${icons[sessionFileIconName(file.kind)]}</span
                              >`}
                          <span class="fleet-session-uploads__meta">
                            <span class="fleet-session-uploads__name">${file.filename}</span>
                            <span class="fleet-session-uploads__facts muted">${factsLine(file)}</span>
                            ${prepLine(file)
                              ? html`<span class="fleet-session-uploads__prep muted"
                                  >${prepLine(file)}</span
                                >`
                              : nothing}
                          </span>
                          ${isImage ? nothing : html`<span class="sr-only">${file.mediaType}</span>`}
                        </a>
                        ${props.onOpenInTranscript
                          ? html`
                              <button
                                type="button"
                                class="btn btn--sm"
                                data-testid="session-upload-jump"
                                data-session-file-id=${file.fileId}
                                title="Jump to the message this file was attached to"
                                @click=${() => props.onOpenInTranscript?.(file)}
                              >
                                In chat
                              </button>
                            `
                          : nothing}
                      </li>
                    `;
                  })}
                </ul>
              `
      }
    </div>
  `;
}